/**
 * `Composer` — a pílula de pedido (palco da home, dock da conversa, painel da viagem),
 * canonizada do design-hub em 2026-08-20. Portada da cópia do ui-kit do repo de design
 * (`VOLL-OS/voll-intelligence-design`, `packages/ui-kit/src/componentes/composer.tsx`,
 * handoff `design/src/handoff/HANDOFF_COMPOSER.md` de 2026-08-25) para produção em
 * 2026-09-07 (#2670). As duas cópias divergem desde o fork de 2026-08-11 (ver
 * `AGENTS.md`): o que este lado ACRESCENTOU ao contrato do design está marcado
 * "PRODUÇÃO" abaixo, e vale como pauta para o time de design — o resto é 1:1.
 *
 * O que mora aqui é ESTRUTURA e comportamento de interface; todo texto (aria-labels,
 * tooltips, título do painel, frases do placeholder e das sugestões) entra por prop —
 * o kit não tem copy. Três camadas opcionais sobre a pílula base:
 *
 * - `TypewriterPlaceholder`: o placeholder que digita e apaga sozinho, com caret fake
 *   (veredito de 2026-08-25 — substituiu a cascata letra a letra). Veio JUNTO do
 *   Composer (e não como peça separada no consumidor) porque os dois estados que o regem
 *   (`visible` = campo vazio, `animating` = sem foco) são estado INTERNO da pílula —
 *   mantê-lo fora obrigaria a exportar esse estado. Frases por prop.
 * - `SuggestionsPanel` (spotlight): grupos de pedidos prontos revelados no FOCO, em
 *   overlay — a home não muda de altura. Clique/Enter PREENCHE (nunca envia), ↑↓
 *   percorre, Esc fecha até o próximo foco, digitar esconde.
 * - `ComposerDictation`: os ESTADOS VISUAIS do ditado padrão ChatGPT
 *   [✕ | trilho pontilhado + onda | ⏹ | ↑ brand], atalhos Esc/⌃⇧D. SEM IO (decisão do
 *   designer, 2026-08-20): captura de microfone/reconhecimento/desenho da onda são do
 *   CONSUMIDOR, plugados pelo contrato `DictationCaptureHook` abaixo.
 *
 * PRODUÇÃO (acrescentos deste lado, todos opt-in — sem eles o componente é o do design):
 * - O campo é o campo de NÓS (`composer_mentions.tsx`, um editor Lexical em texto puro com
 *   o token de menção) com auto-grow até 200px: Enter envia, Shift+Enter quebra linha. No
 *   toque (`pointer: coarse`) o Enter do teclado virtual QUEBRA LINHA e o envio é só pelo
 *   botão, como o composer do Claude no celular (ver `fieldKeyHandler`).
 *   A conversa recebe pedidos de várias linhas desde sempre e a pílula não pode regredir.
 *   `min-h-13` no lugar do `h-13`: 52px em repouso, crescendo com o texto.
 * - A casca EXPANDE quando o texto PASSA DE UMA LINHA na largura da pílula recolhida,
 *   como o composer do app do Claude: o campo toma a linha inteira e os controles (o `+`,
 *   o mic, o Enviar) descem para uma linha própria abaixo dele; a stadium shape vira
 *   `rounded-3xl` (a mesma regra do `header`: a curva de 100px engolia a primeira e a
 *   última linha). Foco sozinho NÃO expande (decisão de 2026-09-28 — antes expandia ao
 *   entrar em uso, e a pílula pulava de forma a cada toque no celular); enquanto o texto
 *   couber numa linha recolhida a pílula fica como está, e volta a ela quando o texto
 *   encolhe. Quem mede é o `useAutoGrow`, sempre contra a largura RECOLHIDA (histerese:
 *   expandida, o campo é mais largo e o mesmo texto refluiria para uma linha — medir a
 *   largura atual oscilaria). É só CSS sobre a mesma árvore (`flex-wrap` + `order`): o
 *   campo nunca remonta, e o rascunho e o foco sobrevivem à troca. O design tem só uma
 *   linha — este estado é agenda de handoff.
 * - `phrases` é opcional e `placeholder` estático existe: em conversa e em viagem o
 *   texto-guia é fixo (tabela 4 da #2344) — o typewriter é gesto de primeiro contato.
 * - `disabled` (sessão ainda conectando) e `header` (a faixa do `question_prompt` de slot
 *   `composer`, DENTRO da casca — uma borda, um raio, uma sombra; com ela a pílula
 *   vira `rounded-3xl`, porque uma stadium shape a 150px de altura é um comprimido).
 * - `ComposerHandle.focus()` ao lado de `fill()` — o "Nova viagem" foca o campo recém-montado.
 * - `DictationCapture.collect` pode ser ASSÍNCRONA (a transcrição é no servidor), e o hook
 *   devolve também `meter` (o timer da gravação) e `error` (mic negado): o composer mostra
 *   `labels.transcribing` enquanto espera e a mensagem de erro no lugar do resultado. O
 *   aviso é FAIXA própria dentro da casca, abaixo da linha de controles (#2630): na linha,
 *   ao lado do mic, ele disputava a largura com o campo e a 360px espremia o campo a uma
 *   palavra (ou uma letra) por linha. Na linha só entram controles de largura fixa.
 * - Os atalhos (⌃⇧D/Esc) escutam na PRÓPRIA casca, não em `window`: o app monta até três
 *   composers ao mesmo tempo (home, dock, painel da viagem) e um listener global acenderia
 *   três microfones. Preso à casca, o alvo é sempre o composer em que o foco está.
 * - Foco gerido na troca de casca do ditado: o controle clicado DESMONTA (mic → barra →
 *   mic) e o foco cairia no `body`; gravando ele vai para Parar, cancelar devolve ao mic,
 *   Parar devolve ao campo (onde a colheita cai) — só com ponteiro fino: no toque
 *   (`pointer: coarse`) focar o campo abriria o teclado virtual sobre um texto que a
 *   pessoa acabou de DITAR, e a colheita fica no campo sem foco (2026-09-28).
 * - `mentions` (#2907): o contrato de menções é OBRIGATÓRIO — tokens inline de
 *   `@viajante`/`@local`/`@data`/`@viagem` que viajam estruturados em `onSend(text,
 *   mentions)`; a alça tem `insertMention` e `openMentionMenu` (o caminho do `+`). O
 *   `<textarea>` que servia quem não passava a prop saiu quando o último consumidor migrou
 *   (#2934): um campo só, um caminho por gesto.
 */
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ComponentType,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type Ref,
  type RefObject,
} from 'react';
import {
  ArrowUpIcon,
  ArrowElbowDownLeftIcon,
  MicrophoneIcon,
  PlusIcon,
  XIcon,
} from '@phosphor-icons/react';
import { cn } from '../lib/utils';
import { InputGroup, InputGroupAddon, InputGroupButton } from '../ui/input-group';
import { Kbd } from '../ui/kbd';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import {
  ComposerRichField,
  type ComposerMention,
  type ComposerMentions,
  type ComposerRichFieldHandle,
} from './composer_mentions';
import { isCoarsePointer, shellShape, useAutoGrow, useExpandMotion } from './composer_shell';

export type { ComposerMention, ComposerMentionMenu, ComposerMentions } from './composer_mentions';

export type ComposerSuggestion = { Icon: ComponentType<{ className?: string }>; phrase: string };
export type ComposerSuggestionGroup = { title: string; items: ComposerSuggestion[] };

/** Alça imperativa — chips externos preenchem e focam o campo por aqui, sem o estado do
 *  texto subir pra quem hospeda. Preencher NUNCA envia. */
export interface ComposerHandle {
  /** PRODUÇÃO (#4011): com `mentions`, os tokens voltam no lugar dos seus rótulos (o
   *  rascunho que atravessa de tela — ver `ComposerRichFieldHandle.setText`). */
  fill: (phrase: string, mentions?: readonly ComposerMention[]) => void;
  /** PRODUÇÃO: foca o campo (o pedido "Nova viagem" quer o cursor pronto ao abrir). */
  focus: () => void;
  /** PRODUÇÃO (#2907): insere um token no caret — o `+` do consumidor escolhe a menção
   *  fora do campo e a entrega por aqui. */
  insertMention: (mention: ComposerMention) => void;
  /** PRODUÇÃO (#2907, #6863): o `+` do consumidor — foca e abre o menu do gatilho pelo FIM
   *  do texto, sem a pessoa digitar. Escreve o gatilho (com um espaço antes, se o texto não
   *  terminava em espaço) só quando o fim ainda não é um gatilho pendente (`@`, `@consulta`):
   *  aí só leva o caret ao fim, e o menu reabre. Preserva os tokens. */
  openMentionMenu: () => void;
}

/** O que sai do campo num envio: o texto (tokens como o literal que ocupam) e os tokens. */
type FieldContent = { text: string; mentions: ComposerMention[] };

/** O que o hook de captura devolve ao composer enquanto o ditado está de pé. */
export type DictationCapture = {
  /** A COLHEITA, chamada no Parar/Enviar. PRODUÇÃO: pode ser uma Promise (transcrição no
   *  servidor) — o composer mostra `labels.transcribing` até ela resolver e trata a
   *  rejeição como falha (`labels.dictationFailed`). */
  collect: () => string | Promise<string>;
  /** PRODUÇÃO: texto curto ao lado da onda — o timer da gravação (há um teto de duração e
   *  sem ele não há como saber quanto falta). Ausente, nada é desenhado. */
  meter?: string;
  /** PRODUÇÃO: falha ANTES de colher (permissão de microfone negada, gravador indisponível),
   *  já localizada pelo consumidor. Ao aparecer, o composer sai do ditado e a exibe. */
  error?: string;
};

/** O contrato do ditado sem IO: o consumidor pluga um HOOK que recebe o canvas da onda,
 *  faz a captura e o desenho que quiser (ou nada — o trilho fica), e devolve a colheita.
 *  O kit chama o hook incondicionalmente no topo do componente de ditado, então as regras
 *  de hooks valem pra implementação — e a montagem do componente É o início da gravação. */
export type DictationCaptureHook = (
  canvasRef: RefObject<HTMLCanvasElement | null>,
) => DictationCapture;

export type ComposerLabels = {
  /** aria-label do campo — fixo, é o nome acessível (o placeholder animado não serve). */
  input: string;
  attach: string;
  send: string;
  /** Obrigatórios só com a camada correspondente ligada. PRODUÇÃO: sem `dictate` o
   *  mic não é desenhado (nem como vitrine). */
  dictate?: string;
  suggestionsTitle?: string;
  dictationCancel?: string;
  dictationStop?: string;
  dictationSend?: string;
  /** PRODUÇÃO: o aviso enquanto uma `collect` assíncrona não resolve, e o da rejeição. */
  transcribing?: string;
  dictationFailed?: string;
};

/* Ritmo do typewriter (veredito do estudo `Carrossel do Placeholder`, 2026-08-25):
   digita a 55ms por letra, segura a frase inteira 2s, apaga a 22ms por letra. */
const TYPE_MS = 55;
const DELETE_MS = 22;
const HOLD_MS = 2000;

/* PRODUÇÃO: o respiro vertical dos addons. O `InputGroupAddon` nasce com `py-1.5`, e
   36px de botão + 12 dariam 48 — acima dos 44 que o app pede à pílula no mobile
   (`pillClassName`). Com `py-1` o addon fecha em 44 e a altura de repouso volta a
   ser decidida pelo `min-h` da pílula, nos dois breakpoints. Na casca EXPANDIDA os addons
   caem para a linha de baixo e entram esmaecidos, assentando junto com a casca que a
   `useExpandMotion` faz crescer (`composer_shell.ts`; a utilitária mora em `styles.css`).
   Os addons da barra do ditado herdam a classe sem efeito: gravando, a casca não expande. */
export const ADDON = 'py-1 group-data-[expanded=true]/input-group:composer-row-in';

/* PRODUÇÃO: o clique num controle da casca (o `+`, o mic, o Enviar) não pode roubar o
   foco do campo — o blur no meio do gesto fecharia a casca expandida ANTES do click, e o
   botão sairia de sob o ponteiro (no celular, ainda derrubaria o teclado). `mousedown`
   com `preventDefault` é a mesma defesa do menu do `@` e do `SuggestionsPanel`; o foco de
   teclado (Tab) não passa por aqui. Só nos ADDONS: no campo o mousedown é o que posiciona
   o caret. */
const keepFieldFocus = (e: ReactMouseEvent) => {
  e.preventDefault();
};

/* PRODUÇÃO — o campo na casca EXPANDIDA: primeiro na linha (antes do addon esquerdo, que
   perde o `order-first` dele) e ocupando-a inteira, com o recuo da linha inteira (`px-1.5`
   + o `px-2.5` do campo = 16px, alinhado ao `+` da linha de baixo) e um respiro em cima.
   Variante `group-data-*` lida do `data-expanded` da pílula, sem prop descendo a árvore. */
const EXPANDED_FIELD =
  'group-data-[expanded=true]/input-group:order-first group-data-[expanded=true]/input-group:basis-full group-data-[expanded=true]/input-group:px-1.5 group-data-[expanded=true]/input-group:pt-1';

/* Placeholder typewriter — camada SOBREPOSTA, não o atributo `placeholder` (o caret
   fake e o recorte por letra pedem elementos próprios; o nome acessível é o aria-label).
   PARA no foco mas não desaparece: congela o recorte que estava (o caret fake some — o
   caret real assume) e some de verdade no primeiro caractere digitado. Substituiu a
   cascata letra a letra no veredito de 2026-08-25. */
export function TypewriterPlaceholder({
  phrases,
  visible,
  animating,
}: {
  phrases: [string, ...string[]];
  visible: boolean;
  animating: boolean;
}) {
  const [reduced] = useState(() => matchMedia('(prefers-reduced-motion: reduce)').matches);
  const [index, setIndex] = useState(0);
  const [length, setLength] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const phrase = phrases[index] ?? phrases[0];

  useEffect(() => {
    if (!visible || !animating || reduced) return undefined;
    /* `>=`, não `===`: se `phrases` trocar pra uma frase mais curta com o recorte já
       além do fim, o ciclo fecha e deleta em vez de incrementar pra sempre. */
    const full = length >= phrase.length;
    const id = setTimeout(
      () => {
        if (!deleting && full) {
          setDeleting(true);
        } else if (deleting && length === 0) {
          setDeleting(false);
          setIndex((n) => (n + 1) % phrases.length);
        } else {
          setLength((n) => n + (deleting ? -1 : 1));
        }
      },
      !deleting && full ? HOLD_MS : deleting ? DELETE_MS : TYPE_MS,
    );
    return () => {
      clearTimeout(id);
    };
  }, [visible, animating, reduced, deleting, length, phrase.length, phrases.length]);

  if (!visible) return null;
  return (
    /* `inset-0` no invólucro interno (que já nasce depois do padding) + `px-2.5` do campo —
       a frase nasce exatamente onde o texto digitado nasce, com qualquer recuo do
       invólucro, e TERMINA onde o campo termina: sem o `right-0` a camada tinha a largura
       do próprio texto e, em pílula estreita (~290px), a frase corria por baixo do mic;
       `truncate` na frase recorta com reticências. Tamanho declarado (text-body), não
       herdado. */
    <span
      aria-hidden
      className="text-muted-foreground pointer-events-none absolute inset-0 flex items-center overflow-hidden px-2.5 text-body whitespace-nowrap select-none"
    >
      <span className="truncate">{reduced ? phrase : phrase.slice(0, length)}</span>
      {/* Caret fake pulsando — só enquanto a máquina roda; no foco o caret real assume.
          h-4.5 = 1.2em do text-body (15px) no degrau da escala, sem valor cru. */}
      {!reduced && animating ? (
        <span className="ml-0.5 inline-block h-4.5 w-px animate-pulse bg-current" />
      ) : null}
    </span>
  );
}

/* O acorde do ditado. `e.code === 'KeyD'` e não `e.key`: com Shift preso o `key` vira
   'D' maiúsculo, e em teclado não-US vira o caractere daquele layout — o `code` é a
   POSIÇÃO física da tecla, que é o que um acorde significa. */
const isDictationChord = (e: ReactKeyboardEvent) => e.ctrlKey && e.shiftKey && e.code === 'KeyD';

/* A jornada do ditado dentro da pílula: [✕ | canvas da onda (+ timer) | ⏹ | ↑ brand].
   Tooltips com o atalho em `Kbd` — os literais ESC/⌃⇧D espelham os atalhos fixos deste
   componente. Os atalhos em si são tratados pela casca (`onKeyDown` do Composer), que
   chama os mesmos `onCancel`/`onStop` dos botões — teclado e mouse não podem divergir. */
export function ComposerDictation({
  useCapture,
  labels,
  onCancel,
  onStop,
  onSend,
  onError,
  stopRef,
}: {
  useCapture: DictationCaptureHook;
  labels: Pick<ComposerLabels, 'dictationCancel' | 'dictationStop' | 'dictationSend'>;
  onCancel: () => void;
  onStop: (text: string | Promise<string>) => void;
  onSend: (text: string | Promise<string>) => void;
  onError: (message: string) => void;
  /** O Parar recebe o foco ao montar (o mic que foi clicado saiu de cena) e é o alvo do
   *  ⌃⇧D da casca. */
  stopRef: RefObject<HTMLButtonElement | null>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // eslint-disable-next-line react/hooks -- the host injects a stable hook through the port; moving the port to a component or store is #6816
  const capture = useCapture(canvasRef);
  useEffect(() => {
    if (capture.error) onError(capture.error);
  }, [capture.error, onError]);
  useEffect(() => {
    stopRef.current?.focus();
  }, [stopRef]);
  return (
    <>
      <InputGroupAddon className={cn(ADDON, 'pl-3.5')}>
        <Tooltip>
          <TooltipTrigger
            render={
              <InputGroupButton
                size="icon-md"
                aria-label={labels.dictationCancel}
                onClick={onCancel}
              >
                <XIcon className="size-4.5" />
              </InputGroupButton>
            }
          />
          <TooltipContent side="bottom">
            {labels.dictationCancel} <Kbd>ESC</Kbd>
          </TooltipContent>
        </Tooltip>
      </InputGroupAddon>
      <div className="min-w-0 flex-1" aria-hidden>
        <canvas ref={canvasRef} className="text-muted-foreground block h-7 w-full" />
      </div>
      <InputGroupAddon align="inline-end" className={cn(ADDON, 'gap-1.5 pr-3.5')}>
        {capture.meter ? (
          <span className="text-caption text-muted-foreground tabular-nums">{capture.meter}</span>
        ) : null}
        <Tooltip>
          <TooltipTrigger
            render={
              <InputGroupButton
                ref={stopRef}
                size="icon-md"
                aria-label={labels.dictationStop}
                onClick={() => {
                  onStop(capture.collect());
                }}
                className="ring-foreground/10 ring-1"
              >
                {/* O quadrado do stop é forma, não glifo — o `Square` da lucide vem
                    vazado, e a referência é sólida. */}
                <span className="bg-foreground size-2.5 rounded-xs" />
              </InputGroupButton>
            }
          />
          <TooltipContent side="bottom">
            {labels.dictationStop} <Kbd>⌃⇧D</Kbd>
          </TooltipContent>
        </Tooltip>
        <InputGroupButton
          size="icon-md"
          aria-label={labels.dictationSend}
          onClick={() => {
            onSend(capture.collect());
          }}
          className="bg-(--brand) text-white hover:bg-(--brand)/85"
        >
          <ArrowUpIcon className="size-4.5" />
        </InputGroupButton>
      </InputGroupAddon>
    </>
  );
}

export type Status = { text: string; error: boolean } | null;

/* O canto direito da pílula. O mic FICA quando o texto entra (mic e Enviar convivem —
   dá pra continuar ditando por cima do que já foi escrito). Sem `onDictate` o mic é
   vitrine, como sempre foi. PRODUÇÃO: o mic descansa enquanto a colheita não resolve, e
   SEM `labels.dictate` não há mic — um botão só-ícone exige nome (regra do `Button`), e um
   app que não oferece ditado (host sem microfone) não quer uma vitrine morta no lugar
   dele. Aqui só entram controles de largura FIXA: texto (o aviso do ditado) mora na
   `DictationStatus`, fora da linha — é o que garante ao campo a largura que sobra.
   Na casca EXPANDIDA (`data-expanded` na pílula, lido pela variante `group-data-*` como
   o `disabled` do `InputGroupAddon`) o canto vai ao fim da linha de controles
   (`ml-auto`), onde o campo já não está para empurrá-lo. */
export function SendOrDictate({
  hasText,
  disabled,
  micDisabled,
  send,
  onDictate,
  labels,
  micRef,
}: {
  hasText: boolean;
  disabled: boolean;
  /** O mic descansa também enquanto uma colheita assíncrona não resolve. */
  micDisabled: boolean;
  send: () => void;
  onDictate?: () => void;
  labels: Pick<ComposerLabels, 'send' | 'dictate'>;
  micRef: RefObject<HTMLButtonElement | null>;
}) {
  const mic = labels.dictate ? (
    <InputGroupButton
      ref={micRef}
      size="icon-md"
      aria-label={labels.dictate}
      disabled={micDisabled}
      {...(onDictate ? { onClick: onDictate } : {})}
    >
      <MicrophoneIcon className="size-4.5" />
    </InputGroupButton>
  ) : null;
  return (
    <InputGroupAddon
      align="inline-end"
      className={cn(ADDON, 'gap-1.5 pr-3.5 group-data-[expanded=true]/input-group:ml-auto')}
      onMouseDown={keepFieldFocus}
    >
      {mic && onDictate ? (
        <Tooltip>
          <TooltipTrigger render={mic} />
          <TooltipContent side="bottom">
            {labels.dictate} <Kbd>⌃⇧D</Kbd>
          </TooltipContent>
        </Tooltip>
      ) : (
        mic
      )}
      {hasText ? (
        <InputGroupButton
          size="icon-md"
          aria-label={labels.send}
          disabled={disabled}
          onClick={send}
          className="bg-(--brand) text-white hover:bg-(--brand)/85"
        >
          <ArrowUpIcon className="size-4.5" />
        </InputGroupButton>
      ) : null}
    </InputGroupAddon>
  );
}

/* PRODUÇÃO — o aviso do ditado (transcrevendo / falha) como FAIXA da casca, irmã da linha
   de controles (#2630). A live region existe SEMPRE (vazia em repouso): leitor de tela só
   anuncia mudança dentro de uma região já montada; `empty:hidden` some com a faixa sem
   desmontá-la. Irmã, e não filha da linha: a árvore acima do campo não muda quando o aviso
   entra ou sai — o auto-grow e o foco dependem de o campo não remontar. O recuo
   horizontal (`px-7`) é onde o texto do campo nasce sem addon esquerdo: `pl-4.5` do
   invólucro + `px-2.5` do campo — o aviso lê como legenda do que foi (ou não) ditado. */
export function DictationStatus({ status }: { status: Status }) {
  return (
    <div
      role="status"
      className={cn(
        'text-caption px-7 pb-2.5 empty:hidden',
        status?.error ? 'text-destructive' : 'text-muted-foreground',
      )}
    >
      {status?.text}
    </div>
  );
}

/* O painel do spotlight — overlay ancorado no invólucro do composer, nunca empurra a
   página. SEM caixa (referência Claude Cowork): as ideias moram soltas; o fundo é um véu
   `bg-background` invisível que só existe pra cobrir o que vive atrás, e o `min-h`
   garante a cobertura. Um rótulo só — grupos separados liam como formulário. */
export function SuggestionsPanel({
  groups,
  items,
  active,
  title,
  onActive,
  onPick,
}: {
  groups: ComposerSuggestionGroup[];
  items: ComposerSuggestion[];
  active: number;
  title: string | undefined;
  onActive: (i: number) => void;
  onPick: (phrase: string) => void;
}) {
  return (
    /* Véu em FADE puro (`--enter-distance: 0`) — véu que desliza arrastaria as linhas. */
    <div
      className="bg-background enter absolute inset-x-0 top-full z-20 mt-6 min-h-80"
      style={{ '--enter-distance': '0rem' } as CSSProperties}
    >
      <div
        className="text-caption text-muted-foreground enter mb-2 px-2 font-normal"
        style={{ animationDelay: 'var(--enter-step)' }}
      >
        {title}
      </div>
      <div className="grid gap-0.5">
        {groups
          .flatMap((g) => g.items)
          .map((s) => {
            const i = items.indexOf(s);
            return (
              <button
                key={s.phrase}
                type="button"
                /* `onMouseDown` + preventDefault, não `onClick`: o clique dispara o blur
                   do campo ANTES do click — a lista fecharia sem escolher. */
                onMouseDown={(e) => {
                  e.preventDefault();
                  onPick(s.phrase);
                }}
                onMouseEnter={() => {
                  onActive(i);
                }}
                /* Cascata da casa com delay por índice; hover em MEIO tom (bg-muted/50). */
                className={`enter flex w-full cursor-pointer items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors duration-100 ${
                  i === active ? 'bg-muted/50' : ''
                }`}
                style={{ animationDelay: `calc(${String(i + 2)} * var(--enter-step))` }}
              >
                {/* O tile dá corpo de ilustração ao glifo. */}
                <span className="bg-muted/60 ring-foreground/5 flex size-9 shrink-0 items-center justify-center rounded-lg ring-1">
                  <s.Icon className="text-muted-foreground size-4.5" aria-hidden />
                </span>
                <span className="text-body text-foreground min-w-0 flex-1 truncate">
                  {s.phrase}
                </span>
                {/* O ↵ anuncia o Enter — só na linha ativa, com espaço reservado. */}
                <ArrowElbowDownLeftIcon
                  className={`text-muted-foreground size-3.5 shrink-0 transition-opacity duration-100 ${
                    i === active ? 'opacity-100' : 'opacity-0'
                  }`}
                  aria-hidden
                />
              </button>
            );
          })}
      </div>
    </div>
  );
}

/* O addon esquerdo resolvido numa tacada (fora do `Composer` pra caber no teto de
   complexidade): `leftAddon` explícito prevalece; `withAttach` desligado some com o
   botão e devolve o respiro maior pro texto. Na casca EXPANDIDA (variante
   `group-data-[expanded=true]`) o addon perde o `order-first` do `InputGroupAddon` — é o
   campo quem vai primeiro, sozinho na linha de cima — e o campo ganha o recuo próprio da
   linha inteira (`EXPANDED_PAD`). */
function addonEsquerdo(leftAddon: ReactNode, withAttach: boolean, label: string) {
  // `null` conta como ausente: `leftAddon={cond ? <X /> : null}` com `withAttach`
  // desligado não pode devolver o botão de anexo default.
  if (leftAddon == null && !withAttach) return { no: null, pad: 'pl-4.5' };
  return {
    no: (
      <InputGroupAddon
        className={cn(ADDON, 'pl-3.5 group-data-[expanded=true]/input-group:order-none')}
        onMouseDown={keepFieldFocus}
      >
        {leftAddon ?? (
          <InputGroupButton size="icon-md" aria-label={label}>
            <PlusIcon className="size-4.5" />
          </InputGroupButton>
        )}
      </InputGroupAddon>
    ),
    pad: 'pl-1.5',
  };
}

/* PRODUÇÃO — a máquina do ditado vista da casca: entrar, cancelar, colher (síncrono ou
   assíncrono) e para onde o foco volta em cada saída. Hook próprio para o `Composer`
   caber no teto de linhas/complexidade. A colheita chega CRUA a `deliverFill`/`deliverSend`:
   quem junta ao rascunho é o campo, appendando sem achatar os tokens (o campo segue
   montado, fora de cena, durante o ditado). */
export function useDictationFlow({
  labels,
  deliverFill,
  deliverSend,
  focusField,
  micRef,
}: {
  labels: Pick<ComposerLabels, 'transcribing' | 'dictationFailed'>;
  deliverFill: (dictated: string) => void;
  deliverSend: (dictated: string) => void;
  focusField: () => void;
  micRef: RefObject<HTMLButtonElement | null>;
}) {
  const [dictating, setDictating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<Status>(null);
  const focusMicWhenFree = useRef(false);
  useEffect(() => {
    if (busy || !focusMicWhenFree.current) return;
    focusMicWhenFree.current = false;
    micRef.current?.focus();
  }, [busy, micRef]);
  // Para onde o foco volta quando a barra do ditado desmonta — marcado no gesto (é
  // quando se sabe o destino) e consumido quando o controle de destino REMONTA:
  // cancelar/falhar devolvem ao mic, colher devolve ao campo, onde a colheita cai.
  // No toque a colheita NÃO devolve ao campo: focar abriria o teclado virtual sobre o
  // texto que a pessoa acabou de ditar (o mic é botão — focá-lo não abre teclado).
  // Lido uma vez, como o `enterSends` da casca: o dedo não vira mouse no meio do uso.
  const [touch] = useState(isCoarsePointer);
  const returnFocus = useRef<'mic' | 'field' | null>(null);
  useEffect(() => {
    if (dictating || !returnFocus.current) return;
    const target = returnFocus.current;
    returnFocus.current = null;
    if (target === 'mic') micRef.current?.focus();
    else focusField();
  }, [dictating, micRef, focusField]);

  const start = useCallback(() => {
    setStatus(null);
    setDictating(true);
  }, []);
  const cancel = useCallback(() => {
    returnFocus.current = 'mic';
    setDictating(false);
  }, []);
  const fail = useCallback((message: string) => {
    returnFocus.current = 'mic';
    setDictating(false);
    setStatus({ text: message, error: true });
  }, []);
  /* A colheita, síncrona ou não, passa pelo mesmo funil: `deliver` decide o destino
     (Parar → campo; ↑ → envio), sempre sobre o rascunho + o ditado. Uma Promise liga o
     aviso até resolver; a rejeição vira o aviso de falha e a pessoa grava de novo ou
     digita (sem retry automático). O rascunho é lido pelo campo na ENTREGA, não aqui: com
     a colheita assíncrona o campo já voltou e segue editável enquanto "Transcrevendo…"
     aparece — ler antes descartaria o que a pessoa digitou nessa janela (#2688). */
  const harvest = useCallback(
    (collected: string | Promise<string>, deliver: (dictated: string) => void) => {
      returnFocus.current = touch ? null : 'field';
      setDictating(false);
      if (typeof collected === 'string') {
        deliver(collected);
        return;
      }
      setBusy(true);
      setStatus(labels.transcribing ? { text: labels.transcribing, error: false } : null);
      collected
        .then((text) => {
          setStatus(null);
          deliver(text);
        })
        .catch(() => {
          setStatus(labels.dictationFailed ? { text: labels.dictationFailed, error: true } : null);
          // Falhou: o foco vai ao mic, o gesto de tentar de novo — o campo já o tinha
          // recebido ao sair do ditado, como em toda colheita (achado do review, #2688).
          // Marcado aqui e aplicado no efeito abaixo: neste instante o mic ainda está
          // desabilitado (`busy`), e botão desabilitado não recebe foco.
          focusMicWhenFree.current = true;
        })
        .finally(() => {
          setBusy(false);
        });
    },
    [labels.transcribing, labels.dictationFailed, touch],
  );
  const stop = useCallback(
    (collected: string | Promise<string>) => {
      harvest(collected, deliverFill);
    },
    [harvest, deliverFill],
  );
  const sendDictated = useCallback(
    (collected: string | Promise<string>) => {
      harvest(collected, deliverSend);
    },
    [harvest, deliverSend],
  );
  return { dictating, busy, status, start, cancel, fail, stop, sendDictated };
}

export type Voice = ReturnType<typeof useDictationFlow>;

/* O spotlight visto do campo: ↑↓ percorre, Esc fecha até o próximo foco, Enter numa
   sugestão ativa PREENCHE — só sem sugestão ativa o Enter envia (Shift+Enter quebra
   linha). `enterSends` desliga o envio pelo Enter: no toque (`pointer: coarse`) a tecla
   do teclado virtual quebra linha e o envio é só pelo botão, como o composer do Claude
   no celular — não há Shift para pedir a quebra, e o "return" que manda a mensagem
   surpreende. Fábrica fora do componente pelo teto de complexidade. */
function fieldKeyHandler({
  listOpen,
  items,
  active,
  setActive,
  closeList,
  fill,
  submit,
  enterSends,
}: {
  listOpen: boolean;
  items: ComposerSuggestion[];
  active: number;
  setActive: (update: (n: number) => number) => void;
  closeList: () => void;
  fill: (phrase: string) => void;
  submit: () => void;
  enterSends: boolean;
}) {
  return (e: ReactKeyboardEvent) => {
    if (listOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      const down = e.key === 'ArrowDown';
      // ↑ sem linha ativa (-1) vai para a última, como do zero.
      setActive((n) => (down ? (n + 1) % items.length : n <= 0 ? items.length - 1 : n - 1));
      return;
    }
    if (listOpen && e.key === 'Escape') {
      closeList();
      return;
    }
    if (e.key !== 'Enter' || e.shiftKey) return;
    const picked = listOpen && active >= 0 ? items[active] : undefined;
    // Sem sugestão ativa e sem envio pelo Enter, a tecla fica com o editor (quebra linha).
    if (!picked && !enterSends) return;
    e.preventDefault();
    if (picked) fill(picked.phrase);
    else submit();
  };
}

/* Os atalhos do ditado, na casca (ver o cabeçalho) e em fase de CAPTURA: o tooltip do
   botão Parar (Base UI) trata o Esc antes de ele borbulhar, e o gesto de descartar a
   gravação não pode depender de quem mais escuta a tecla lá dentro. `stopPropagation`
   só quando de fato tratamos: o Esc é disputado no documento (um drawer aberto por cima
   o quer para fechar), e deixá-lo subir depois de descartar a gravação faria um gesto
   fechar duas coisas. Fora do ditado o Esc não é nosso. */
export function onShellKeyDown(
  e: ReactKeyboardEvent,
  voice: Voice,
  canDictate: boolean,
  stopRef: RefObject<HTMLButtonElement | null>,
): void {
  if (voice.dictating && e.key === 'Escape') {
    e.preventDefault();
    e.stopPropagation();
    voice.cancel();
    return;
  }
  if (!isDictationChord(e)) return;
  if (voice.dictating) {
    e.preventDefault();
    e.stopPropagation();
    stopRef.current?.click();
    return;
  }
  if (!canDictate) return;
  e.preventDefault();
  e.stopPropagation();
  voice.start();
}

/* O campo em si — o campo de nós — e a camada do typewriter no MESMO invólucro relativo.
   Componente próprio: a linha de controles não precisa conhecer as refs do Lexical. */
type FieldProps = {
  text: string;
  focused: boolean;
  onChange: (text: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  onKeyDown: (e: ReactKeyboardEvent) => void;
  rich: {
    fieldRef: RefObject<HTMLDivElement | null>;
    handleRef: RefObject<ComposerRichFieldHandle | null>;
    mentions: ComposerMentions;
    menuAnchor: RefObject<HTMLElement | null>;
    onMenuOpenChange: (open: boolean) => void;
  };
};

function ComposerField({
  field,
  phrases,
  placeholder,
  disabled,
  label,
}: {
  field: FieldProps;
  phrases: [string, ...string[]] | undefined;
  placeholder: string | undefined;
  disabled: boolean;
  label: string;
}) {
  const typewriter = phrases ? (
    <TypewriterPlaceholder
      phrases={phrases}
      visible={field.text === '' && !disabled}
      animating={!field.focused}
    />
  ) : null;
  return (
    /* Em fase de CAPTURA, não `onKeyDown`: o Lexical escuta `keydown` no próprio campo e
       trataria o Enter (quebra de linha) antes do handler React em bolha. A captura no
       invólucro chega antes, e `stopPropagation` do que tratamos (envio, ↑↓ do
       spotlight) impede o editor de ver a tecla. */
    <div className="flex min-w-0 flex-1" onKeyDownCapture={field.onKeyDown}>
      <ComposerRichField
        handleRef={field.rich.handleRef}
        fieldRef={field.rich.fieldRef}
        mentions={field.rich.mentions}
        disabled={disabled}
        label={label}
        /* Com `phrases`, quem desenha o repouso é a camada animada e o atributo fica
           vazio; o nome acessível é o aria-label, fixo. */
        placeholder={phrases ? undefined : placeholder}
        onTextChange={field.onChange}
        onFocus={field.onFocus}
        onBlur={field.onBlur}
        onMenuOpenChange={field.rich.onMenuOpenChange}
        menuAnchor={field.rich.menuAnchor}
      >
        {typewriter}
      </ComposerRichField>
    </div>
  );
}

/* O ditado SAI DE CENA a linha de controles, não a desmonta: o rascunho (texto e tokens)
   vive DENTRO do editor Lexical, e desmontar o campo perderia o rascunho
   e a alça — a colheita síncrona entrega antes do commit do remonte e cairia num
   `appendText` sem alça (achado do review, #2907). De pé, `display: contents` devolve os
   filhos à linha como filhos diretos, sem invólucro extra no flex; fora de cena vão o
   ATRIBUTO `hidden` (pela árvore de acessibilidade) E a utilitária `hidden` — o Preflight
   escopado deste pacote não declara `[hidden] { display: none }`, e regra de autor
   (`.contents`) venceria a folha do agente. */
function OffStage({ off, children }: { off: boolean; children: ReactNode }) {
  return (
    <div className={off ? 'hidden' : 'contents'} hidden={off}>
      {children}
    </div>
  );
}

/* A linha de controles em repouso: addon esquerdo, campo (+ typewriter) e o canto
   direito. Componente próprio para a casca caber no teto de linhas. */
function ComposerControls({
  left,
  field,
  phrases,
  placeholder,
  disabled,
  labels,
  voice,
  canDictate,
  micRef,
  send,
}: {
  left: ReturnType<typeof addonEsquerdo>;
  field: FieldProps;
  phrases: [string, ...string[]] | undefined;
  placeholder: string | undefined;
  disabled: boolean;
  labels: ComposerLabels;
  voice: Voice;
  canDictate: boolean;
  micRef: RefObject<HTMLButtonElement | null>;
  send: () => void;
}) {
  return (
    <>
      {left.no}
      {/* O padding fica FORA do invólucro relativo: elemento absoluto ignora padding do
          pai, então o placeholder ancora num box que já começa depois do recuo — campo e
          camada de placeholder andam juntos com qualquer `pad`. Sem o addon esquerdo
          (`withAttach` desligado), o texto respira com pl-4.5. EXPANDIDO, o campo vai
          primeiro (`order-first`, antes do addon esquerdo, que perdeu o dele) e toma a
          linha inteira (`basis-full`): a linha de controles QUEBRA (`flex-wrap`) e os
          addons caem para a de baixo. */}
      <div className={cn('flex flex-1', left.pad, EXPANDED_FIELD)}>
        <ComposerField
          field={field}
          phrases={phrases}
          placeholder={placeholder}
          disabled={disabled}
          label={labels.input}
        />
      </div>
      <SendOrDictate
        hasText={field.text !== ''}
        disabled={disabled}
        micDisabled={disabled || voice.busy}
        send={send}
        labels={labels}
        micRef={micRef}
        {...(canDictate ? { onDictate: voice.start } : {})}
      />
    </>
  );
}

/* O texto da pílula e os gestos sobre ele — preencher (nunca envia), enviar, inserir um
   token e as duas entregas do ditado — mais a alça imperativa. Cada gesto fala com a alça
   do Lexical (`ComposerRichFieldHandle`); o estado `text` é o ESPELHO do campo para o que a
   casca decide por ele (o Enviar aparece, o spotlight some). Hook próprio para a casca
   caber no teto de linhas. */
function useComposerText({
  ref,
  onSend,
  rich,
  grow,
}: {
  ref: Ref<ComposerHandle> | undefined;
  onSend: ((text: string, mentions: ComposerMention[]) => void) | undefined;
  rich: RefObject<ComposerRichFieldHandle | null>;
  grow: () => void;
}) {
  const [text, setText] = useState('');
  const [active, setActive] = useState(-1);
  const focusField = useCallback(() => {
    rich.current?.focus();
  }, [rich]);
  /* Preencher: texto posto, foco mantido, caret no fim; o auto-grow lê a altura no frame
     seguinte, quando o DOM do editor já refletiu o texto. */
  const fill = useCallback(
    (phrase: string, mentions?: readonly ComposerMention[]) => {
      setActive(-1);
      // Foco ANTES do texto: focar um contenteditable move a seleção do DOM para o
      // início dele, e o Lexical sincroniza o caret de volta — escrever depois deixa
      // Lexical e DOM de acordo no fim do texto (o gatilho `@` lê o texto antes do caret).
      rich.current?.focus();
      rich.current?.setText(phrase, mentions);
      requestAnimationFrame(grow);
    },
    [rich, grow],
  );
  const read = useCallback(
    (): FieldContent => rich.current?.read() ?? { text: '', mentions: [] },
    [rich],
  );
  /* Sem `onSend` o campo é vitrine — o contrato das stories de template isoladas. */
  const send = useCallback(() => {
    const content = read();
    const clean = content.text.trim();
    if (!clean) return;
    onSend?.(clean, content.mentions);
    rich.current?.clear();
    requestAnimationFrame(grow);
  }, [read, onSend, rich, grow]);
  const insertMention = useCallback(
    (mention: ComposerMention) => {
      rich.current?.insertMention(mention);
    },
    [rich],
  );
  /* A entrega do ditado ao campo: o que o ditado colheu appenda ao que já estava (padrão
     ChatGPT), sem achatar os tokens. */
  const deliverFill = useCallback(
    (dictated: string) => {
      rich.current?.appendText(dictated);
    },
    [rich],
  );
  /* Foco ANTES de escrever, pela mesma razão do `fill`: focar o campo move a seleção do
     DOM, e escrever depois deixa o caret no fim do que entrou — é onde o gatilho `@`
     precisa estar para o menu abrir. */
  const openMentionMenu = useCallback(() => {
    focusField();
    rich.current?.openTrigger();
  }, [focusField, rich]);
  useImperativeHandle(ref, () => ({ fill, focus: focusField, insertMention, openMentionMenu }), [
    fill,
    focusField,
    insertMention,
    openMentionMenu,
  ]);
  const deliverSend = useCallback(
    (dictated: string) => {
      deliverFill(dictated);
      send();
    },
    [deliverFill, send],
  );
  return { text, setText, active, setActive, fill, send, focusField, deliverFill, deliverSend };
}

/* A linha de controles (`ROW`) em repouso herda o `min-h` da pílula e centra os
   controles (fecha em 44/52); expandida ela QUEBRA em duas — o campo em cima, os
   controles embaixo — e a altura é a soma das duas, sem o piso herdado (que faria a
   linha do campo sozinha medir os 52 da pílula inteira). A forma da casca em si
   (`shellShape`) e o que conta como "em uso" (`inUse`) moram em `composer_shell.ts`. */
const ROW =
  'flex min-h-[inherit] w-full items-center group-data-[expanded=true]/input-group:min-h-0 group-data-[expanded=true]/input-group:flex-wrap';

/* O campo de nós visto da casca: as refs do Lexical, a âncora do menu e a guarda do
   teclado — com o menu do gatilho aberto, Enter/↑↓/Esc são comandos do Lexical, não do
   campo. Hook fora do `Composer` pelo teto de complexidade; é dono do "menu aberto". */
function useRichField({
  mentions,
  fieldRef,
  handleRef,
  menuAnchor,
  onKeyDown,
}: {
  mentions: ComposerMentions;
  fieldRef: RefObject<HTMLDivElement | null>;
  handleRef: RefObject<ComposerRichFieldHandle | null>;
  menuAnchor: RefObject<HTMLElement | null>;
  onKeyDown: (e: ReactKeyboardEvent) => void;
}): Pick<FieldProps, 'rich' | 'onKeyDown'> {
  const menuOpenRef = useRef(false);
  return {
    onKeyDown: (e) => {
      if (menuOpenRef.current) return;
      onKeyDown(e);
      if (e.defaultPrevented) e.stopPropagation();
    },
    rich: {
      fieldRef,
      handleRef,
      mentions,
      menuAnchor,
      onMenuOpenChange: (open) => {
        menuOpenRef.current = open;
      },
    },
  };
}

/* Os eventos do campo vistos da casca (texto, foco, spotlight), montados fora do
   `Composer` pelo teto de linhas. */
function fieldEventsOf({
  setText,
  grow,
  setFocused,
  setEscClosed,
  setActive,
}: {
  setText: (text: string) => void;
  grow: () => void;
  setFocused: (focused: boolean) => void;
  setEscClosed: (closed: boolean) => void;
  setActive: (index: number) => void;
}): Pick<FieldProps, 'onChange' | 'onFocus' | 'onBlur'> {
  return {
    onChange: (next) => {
      setText(next);
      grow();
    },
    onFocus: () => {
      setFocused(true);
      setEscClosed(false);
      setActive(-1);
    },
    onBlur: () => {
      setFocused(false);
    },
  };
}

/* A pílula: InputGroup rounded-full, ring/shadow da casa (`shadow-(--shadow-soft)` — o
   token mora em `.genui2` como custom property de runtime). O send só aparece com texto;
   Enter envia — mas Enter numa sugestão ativa PREENCHE, o envio segue sendo decisão da
   pessoa sobre um texto que ela assumiu. O foco de teclado é o do `InputGroup`: anel
   `ring-ring/50` de 3px, o mesmo de todo campo do DS. */
export function Composer({
  ref,
  className,
  pillClassName,
  onSend,
  phrases,
  placeholder,
  suggestions,
  dictation,
  mentions,
  labels,
  leftAddon,
  withAttach = true,
  header,
  disabled = false,
}: {
  ref?: Ref<ComposerHandle>;
  /** No invólucro (que ancora o spotlight). */
  className?: string;
  /** PRODUÇÃO: na pílula em si — o app estreita a altura de repouso no mobile. */
  pillClassName?: string;
  /** PRODUÇÃO (#2907): o texto (cada token como o literal que ocupa) e os tokens — quem
   *  só lê `text` continua lendo a frase inteira. */
  onSend?: (text: string, mentions: ComposerMention[]) => void;
  /** As frases que o campo "digita" sozinho. PRODUÇÃO: opcional — ausente, vale o
   *  `placeholder` estático (ou nenhum). */
  phrases?: [string, ...string[]];
  /** PRODUÇÃO: texto-guia FIXO (atributo nativo), para onde o typewriter não cabe. */
  placeholder?: string;
  suggestions?: ComposerSuggestionGroup[];
  /** Liga o ditado: o mic ganha ação/atalho e a pílula troca pro modo ditado. */
  dictation?: { useCapture: DictationCaptureHook };
  /** PRODUÇÃO (#2907): o contrato do campo de nós — as opções do menu do gatilho (`@`),
   *  quem o desenha e o rótulo do ✕ dos tokens. Ver `composer_mentions.tsx`. */
  mentions: ComposerMentions;
  labels: ComposerLabels;
  /** Substitui o addon esquerdo default (botão de anexar vitrine). */
  leftAddon?: ReactNode;
  /** `false` some com o botão de anexar (variação pedida no crivo de 2026-08-23) —
   *  a pílula abre direto no texto. Um `leftAddon` explícito prevalece. */
  withAttach?: boolean;
  /** PRODUÇÃO: faixa DENTRO da casca, acima dos controles (o `question_prompt`). */
  header?: ReactNode;
  /** PRODUÇÃO: campo e controles inertes (sessão conectando). */
  disabled?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const [escClosed, setEscClosed] = useState(false);
  const richFieldRef = useRef<HTMLDivElement>(null);
  const richHandleRef = useRef<ComposerRichFieldHandle>(null);
  const micRef = useRef<HTMLButtonElement>(null);
  const stopRef = useRef<HTMLButtonElement>(null);
  // O invólucro é a ÂNCORA do menu do gatilho (ver `MentionTypeaheadPlugin`), e o menu
  // aberto toma Enter/↑↓/Esc (comandos do Lexical), não o campo.
  const shellRef = useRef<HTMLDivElement>(null);
  const pillRef = useRef<HTMLDivElement>(null);
  const getField = useCallback(() => richFieldRef.current, []);
  const getPill = useCallback(() => pillRef.current, []);
  // O texto passa de uma linha (medido pelo auto-grow): é o que expande a casca.
  const [wraps, setWraps] = useState(false);
  const grow = useAutoGrow(getField, getPill, setWraps);
  const { text, setText, active, setActive, fill, send, focusField, deliverFill, deliverSend } =
    useComposerText({ ref, onSend, rich: richHandleRef, grow });
  const items = (suggestions ?? []).flatMap((g) => g.items);
  const listOpen = items.length > 0 && focused && text === '' && !escClosed;
  const voice = useDictationFlow({ labels, deliverFill, deliverSend, focusField, micRef });
  // Gravando não expande: a barra do ditado é de uma linha só.
  const expanded = !voice.dictating && wraps;
  useExpandMotion(pillRef, expanded);
  const shape = shellShape(header != null, voice.status != null, expanded);
  const canDictate = Boolean(dictation) && !disabled && !voice.busy;
  // Lido uma vez, como o reduced-motion do typewriter: o dedo não vira mouse no meio do uso.
  const [enterSends] = useState(() => !isCoarsePointer());
  const onFieldKeyDown = fieldKeyHandler({
    listOpen,
    items,
    active,
    setActive,
    closeList: () => {
      setEscClosed(true);
      setActive(-1);
    },
    fill,
    submit: send,
    enterSends,
  });
  /* O ditado de pé (a camada existe E a gravação está rolando) — uma leitura só, usada
     pela barra e por quem sai de cena. */
  const dictationStage = voice.dictating ? dictation : undefined;
  const richField = useRichField({
    mentions,
    fieldRef: richFieldRef,
    handleRef: richHandleRef,
    menuAnchor: shellRef,
    onKeyDown: onFieldKeyDown,
  });
  return (
    /* O invólucro ancora o painel de sugestões SEM entrar no fluxo (overlay). `z-20`
       porque a cascata `enter` do consumidor cria stacking context. */
    <div
      ref={shellRef}
      className={cn('relative z-20', className)}
      onKeyDownCapture={(e) => {
        onShellKeyDown(e, voice, canDictate, stopRef);
      }}
    >
      {/* Altura e raio da casca animam JUNTOS na `useExpandMotion` (`composer_shell.ts`);
          sem `transition` de raio aqui: de `rounded-full` (infinito) ela ficava clampada
          quase toda a duração e saltava no fim — e o raio EM transição contaminaria a
          medida do destino da FLIP. A redondeza dos botões internos é consequência da forma
          da pílula — atada numa
          tacada (`[&_button]:rounded-full`), não pode divergir quando entrar um quarto
          controle. A casca é UMA (borda, raio, sombra) com ou sem `header`: a faixa
          empilha em coluna sobre a linha de controles (e o aviso do ditado, abaixo), e a
          linha é sempre a mesma árvore — o campo não remonta quando uma faixa entra ou
          sai no meio da conversa. */}
      <InputGroup
        ref={pillRef}
        data-expanded={expanded}
        className={cn(
          'bg-card ring-foreground/10 relative z-10 h-auto min-h-13 flex-col items-stretch border-0 ring-1 shadow-(--shadow-soft) [&_button]:rounded-full',
          shape,
          pillClassName,
        )}
      >
        {header ? <div className="border-border border-b">{header}</div> : null}
        <div className={ROW}>
          {dictationStage ? (
            <ComposerDictation
              // eslint-disable-next-line react/hooks -- the host injects a stable hook through the port; moving the port to a component or store is #6816
              useCapture={dictationStage.useCapture}
              labels={labels}
              onCancel={voice.cancel}
              onStop={voice.stop}
              onSend={voice.sendDictated}
              onError={voice.fail}
              stopRef={stopRef}
            />
          ) : null}
          <OffStage off={Boolean(dictationStage)}>
            <ComposerControls
              left={addonEsquerdo(leftAddon, withAttach, labels.attach)}
              field={{
                text,
                focused,
                ...fieldEventsOf({ setText, grow, setFocused, setEscClosed, setActive }),
                ...richField,
              }}
              phrases={phrases}
              placeholder={placeholder}
              disabled={disabled}
              labels={labels}
              voice={voice}
              canDictate={canDictate}
              micRef={micRef}
              send={send}
            />
          </OffStage>
        </div>
        <DictationStatus status={voice.status} />
      </InputGroup>
      {listOpen ? (
        <SuggestionsPanel
          groups={suggestions ?? []}
          items={items}
          active={active}
          title={labels.suggestionsTitle}
          onActive={setActive}
          onPick={fill}
        />
      ) : null}
    </div>
  );
}
